import type { FlowStep, StepRuntime } from "../types";
import { FlowStepError } from "../engine/errors";
import {
  finalEstimateStatesOn,
  garaazTransitionPath,
  requireEnquiry,
  verifyEq,
  waitForEventWithGate,
} from "./common";
import { idString, liveOptions, pickDeliveryOption, pickSplit } from "./estimateSteps";

export const STEP_FINAL_ESTIMATE_REQUESTED = "final_estimate_requested";
export const STEP_FINAL_ESTIMATE_SHARED = "final_estimate_shared";

const FINAL_ESTIMATE_REQUESTED = "FINAL_ESTIMATE_REQUESTED";
const FINAL_ESTIMATE_SHARED = "FINAL_ESTIMATE_SHARED";

const REQUESTED_OR_LATER = [FINAL_ESTIMATE_REQUESTED, FINAL_ESTIMATE_SHARED];

const SKIP_REASON = "Final estimate states are off: the existing part confirmation and estimate steps apply";

const skipUnlessFinalEstimateStates = (ctx: Parameters<typeof finalEstimateStatesOn>[0]) =>
  finalEstimateStatesOn(ctx) ? null : SKIP_REASON;

const garaazTransition = (rt: StepRuntime, orderId: string, action: string) =>
  rt.api({
    label: `Transition ${action}`,
    method: "POST",
    path: garaazTransitionPath(orderId, action),
    body: {},
  });

/**
 * The enquiry's newest final estimate (anything but the DRAFT_ESTIMATE) from
 * the estimate list. Not GET /v1/estimate/:estimateNo: that one shows every
 * Trading split as the Garaaz supplier. `unreviewedOnly` keeps the backend-built
 * estimate still waiting for the OH's review. Throws when there is none.
 */
const findLatestFinalEstimate = async (
  rt: StepRuntime,
  opts: { unreviewedOnly?: boolean } = {},
): Promise<any> => {
  const enquiry = requireEnquiry(rt.ctx);
  const list = await rt.api<{ estimates?: any[] }>({
    label: "GET estimates",
    method: "GET",
    path: `/v1/estimate?enquiryNo=${enquiry.orderNo}&limit=50`,
  });
  const time = (e: any) => new Date(e?.createdAt || e?.estimateSharedAt || 0).getTime();
  const latest = (list.data?.estimates || [])
    .filter((e) => e?.type !== "DRAFT_ESTIMATE")
    .filter((e) => !opts.unreviewedOnly || (e?.status === "DRAFT" && e?.isReviewed === false))
    .sort((a, b) => time(b) - time(a))[0];
  if (!latest?.estimateNo) {
    throw new FlowStepError(
      opts.unreviewedOnly ? "The enquiry has no final estimate waiting for review" : "The enquiry has no final estimate",
      {
        expected: opts.unreviewedOnly
          ? "A DRAFT final estimate with isReviewed false (built by the backend)"
          : "An estimate that is not a DRAFT_ESTIMATE",
        actual: "none found",
      },
    );
  }
  return latest;
};

/**
 * Fills `ctx.estimate` (estimateNo, split, delivery option) from the enquiry's
 * final estimate the backend built, so the customer confirm step has what it
 * needs. A no-op once it is filled.
 */
const adoptFinalEstimate = async (rt: StepRuntime): Promise<void> => {
  if (rt.ctx.estimate) return;
  const estimate = await findLatestFinalEstimate(rt);
  const preference = rt.ctx.config.preferredDelivery || "STANDARD";
  const split = pickSplit(estimate.splits || [], rt.ctx.supplier.id, preference);
  const splitId = idString(split?._id);
  const option = pickDeliveryOption(liveOptions(split), preference);
  const deliveryOptionId = idString(option?._id ?? option?.id);
  if (!splitId || !deliveryOptionId) {
    throw new FlowStepError(
      `Final estimate ${estimate.estimateNo} has no ${preference} delivery option on the configured supplier's split`,
      {
        expected: "a split with the preferred delivery option",
        actual: JSON.stringify({ splitId, deliveryOptionId }),
      },
    );
  }
  rt.ctx.estimate = { estimateNo: estimate.estimateNo, splitId, deliveryOptionId };
  rt.log(`Using final estimate ${estimate.estimateNo} (split ${splitId})`, "info");
};

export const finalEstimateRequestedStep: FlowStep = {
  id: STEP_FINAL_ESTIMATE_REQUESTED,
  name: "Final estimate requested",
  targetLabel: "Final estimate requested",
  description:
    "Final estimate states on: after part conversion the backend moves the enquiry to FINAL_ESTIMATE_REQUESTED (REQUEST_FINAL_ESTIMATE) and starts the final estimate: supplier selection engine, then part confirmation requests to the picked suppliers (answer them in the supplier app). Checks once, then asks whether to request it from here or wait. Skipped when the final estimate states are off.",
  actor: "EVENT",
  kind: "event",
  estimateMs: [3000, 30000],
  shouldSkip: skipUnlessFinalEstimateStates,
  execute: async (rt) => {
    const enquiry = requireEnquiry(rt.ctx);
    const reached = (status?: string) => REQUESTED_OR_LATER.includes(status ?? "");

    // One check, then ask: nothing moves the enquiry to FER on its own yet, so
    // waiting through several polls first only wastes time.
    const checked = await rt.refreshSnapshot({ enquiry: true });
    const status = checked?.enquiry?.orderStatus ?? rt.ctx.snapshot?.enquiry?.orderStatus;
    if (reached(status)) {
      rt.log(`Enquiry already ${status}`, "info");
    } else {
      const choice = await rt.decide({
        title: `Request ${FINAL_ESTIMATE_REQUESTED} now?`,
        description:
          `The enquiry is ${status ?? "in an unknown state"}. The part conversion handler is meant to move it to ${FINAL_ESTIMATE_REQUESTED}; ` +
          "request it from here (garaaz_transition?action=REQUEST_FINAL_ESTIMATE, needs ff_final_estimate_states on), or wait for the handler.",
        options: [
          {
            id: "request",
            label: "Request it now",
            description: "POST /v1/orders/:id/garaaz_transition?action=REQUEST_FINAL_ESTIMATE",
            tone: "primary",
          },
          { id: "wait", label: "Wait for the handler", description: "Poll the enquiry status", tone: "neutral" },
        ],
      });
      if (choice === "request") {
        await garaazTransition(rt, enquiry.orderId, "REQUEST_FINAL_ESTIMATE");
      } else {
        await waitForEventWithGate(rt, {
          trigger: "Part conversion handler → REQUEST_FINAL_ESTIMATE",
          expected: `enquiry.orderStatus = ${FINAL_ESTIMATE_REQUESTED}`,
          check: (s) => reached(s.enquiry?.orderStatus),
          describe: (s) => `enquiry=${s.enquiry?.orderStatus}`,
          allowContinue: false,
          fallback: {
            label: "Request it from here",
            description: "POST /v1/orders/:id/garaaz_transition?action=REQUEST_FINAL_ESTIMATE (what the handler sends)",
            run: async () => {
              await garaazTransition(rt, enquiry.orderId, "REQUEST_FINAL_ESTIMATE");
            },
          },
        });
      }
    }
  },
  verify: (s) => verifyEq("enquiry.orderStatus", REQUESTED_OR_LATER, s.enquiry?.orderStatus),
};

export const finalEstimateSharedStep: FlowStep = {
  id: STEP_FINAL_ESTIMATE_SHARED,
  name: "Final estimate shared",
  targetLabel: "Final estimate shared",
  description:
    "Final estimate states on: once the suppliers have answered, the backend builds the estimate (isReviewed false) while the enquiry stays FINAL_ESTIMATE_REQUESTED, and the OH reviews it in the OrCA popup: REVIEW ESTIMATE (POST /v1/estimate/:estimateNo/review) shares it and moves the enquiry to FINAL_ESTIMATE_SHARED. Waits for that; on timeout the review can be sent from here. Skipped when the final estimate states are off.",
  actor: "EVENT",
  kind: "event",
  estimateMs: [3000, 120000],
  shouldSkip: skipUnlessFinalEstimateStates,
  execute: async (rt) => {
    if (rt.ctx.snapshot?.enquiry?.orderStatus === FINAL_ESTIMATE_SHARED) {
      rt.log(`Enquiry already ${FINAL_ESTIMATE_SHARED}`, "info");
    } else {
      await waitForEventWithGate(rt, {
        trigger: "Supplier answers → backend builds the estimate → OH REVIEW ESTIMATE in OrCA",
        expected: `enquiry.orderStatus = ${FINAL_ESTIMATE_SHARED}`,
        check: (s) => s.enquiry?.orderStatus === FINAL_ESTIMATE_SHARED,
        describe: (s) => `enquiry=${s.enquiry?.orderStatus}`,
        allowContinue: false,
        fallback: {
          label: "Review it from here",
          description:
            "POST /v1/estimate/:estimateNo/review on the estimate waiting for review (what REVIEW ESTIMATE sends: reviewed → shared → FINAL_ESTIMATE_SHARED)",
          run: async () => {
            const estimate = await findLatestFinalEstimate(rt, { unreviewedOnly: true });
            await rt.api({
              label: "Review final estimate",
              method: "POST",
              path: `/v1/estimate/${estimate.estimateNo}/review`,
              body: {},
            });
          },
        },
      });
    }
    await adoptFinalEstimate(rt);
    rt.log(`Final estimate ${rt.ctx.estimate!.estimateNo} shared`, "success");
  },
  verify: (s) => verifyEq("enquiry.orderStatus", FINAL_ESTIMATE_SHARED, s.enquiry?.orderStatus),
};
